import { RootState } from "core-integration/src/store";
import { getElementStatsByIdentifier } from "core-integration/src/store/element-stats/reducers";
import { IElementStat } from "core-integration/src/store/element-stats/types";
import {
  getElement,
  getHistory,
  getHistoryTotals,
} from "core-integration/src/store/elements/reducers";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { formatNumberToLocaleString } from "core-integration/src/utils/number";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { Cell, Column, Row, Table } from "plos/src/components/Table";
import { InfoText } from "plos/src/components/tooltips/InfoText";
import {
  getElementStat,
  getElementStatNames,
  getStatAbbr,
} from "plos/src/utils/elementStats";
import { TableBody, TableHeader } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  headerCell,
  tableWrapper,
  tHead,
  totalsCell,
  totalsHeaderCell,
} from "../../../../../styles/sharedTableStyles.css";
import { statDetails } from "../../../../../utils/stats";
import {
  oppHeaderCell,
  per90Cell,
  per90HeaderCell,
  tableHeader,
} from "../tableStyles.css";
import HistoryRow from "./HistoryRow";

interface EventHistoryTableProps {
  elementId: number;
}

const EventHistoryTable = ({ elementId }: EventHistoryTableProps) => {
  const { t } = useTranslation();
  const statsByName: Record<string, IElementStat> = useSelector(
    getElementStatsByIdentifier,
  );
  const teamsById = useSelector(getTeamsById);
  const history = useSelector((state: RootState) =>
    getHistory(state, elementId),
  );

  const historyTotals = useSelector((state: RootState) =>
    getHistoryTotals(state, elementId),
  );
  const element = useSelector((state: RootState) =>
    getElement(state, elementId),
  );

  if (!element) {
    return null;
  }

  const statNames = getElementStatNames();
  const { now_cost } = statDetails(t);

  return (
    <div>
      <h2 className={tableHeader}>
        {t("eventHistoryTable.thisSeason", "This Season")}
      </h2>
      {history.length > 0 ? (
        <div className={tableWrapper}>
          <Table
            aria-label={
              t(
                "eventHistoryTable.gameweekHistory",
                "Gameweek history",
              ) as string
            }
          >
            <TableHeader className={tHead}>
              <Column className={headerCell}>GW</Column>
              <Column className={oppHeaderCell} isRowHeader>
                {t("eventHistoryTable.opponent", "Opponent")}
              </Column>
              <Column className={headerCell}>
                {t("eventHistoryTable.result", "Result")}
              </Column>
              <Column className={headerCell}>
                <InfoText label={t("eventHistoryTable.points.label", "Points")}>
                  {t("eventHistoryTable.points.abbr", "Pts")}
                </InfoText>
              </Column>
              {statNames.map((n) => (
                <Column className={headerCell} key={n}>
                  <InfoText label={statsByName[n].label}>
                    {getStatAbbr(n, statsByName)}
                  </InfoText>
                </Column>
              ))}
              <Column className={headerCell}>
                <InfoText
                  label={t(
                    "eventHistoryTable.netTransfers.label",
                    "Net Transfers",
                  )}
                >
                  {t("eventHistoryTable.netTransfers.abbr", "NT")}
                </InfoText>
              </Column>
              <Column className={headerCell}>
                <InfoText
                  label={t(
                    "eventHistoryTable.teamsSelectedBy.label",
                    "Teams selected by",
                  )}
                >
                  {t("eventHistoryTable.teamsSelectedBy.abbr", "TSB")}
                </InfoText>
              </Column>
              <Column className={headerCell}>
                <InfoText label={now_cost.label}>{now_cost.shortName}</InfoText>
              </Column>
            </TableHeader>
            <TableBody>
              {[...history].reverse().map((h) => (
                <HistoryRow
                  key={h.fixture}
                  history={h}
                  teamsById={teamsById}
                  statsByName={statsByName}
                  statNames={statNames}
                />
              ))}
              <Row aria-label={t("eventHistoryTable.totalsRow", "Totals row")}>
                <Cell />
                <Cell className={totalsHeaderCell}>
                  {t("eventHistoryTable.totals", "Totals")}
                </Cell>
                <Cell />
                <Cell className={totalsCell}>{historyTotals.total_points}</Cell>
                {statNames.map((n) => (
                  <Cell className={totalsCell} key={n}>
                    {formatNumberToLocaleString(
                      historyTotals[statsByName[n].name],
                    )}
                  </Cell>
                ))}
                <Cell className={totalsCell}>-</Cell>
                <Cell className={totalsCell}>-</Cell>
                <Cell className={totalsCell}>-</Cell>
              </Row>
              <Row
                aria-label={t(
                  "eventHistoryTable.per90Row",
                  "Per 90 minutes row",
                )}
              >
                <Cell />
                <Cell className={per90HeaderCell}>
                  {t("eventHistoryTable.per90", "Per 90")}
                </Cell>
                <Cell />
                <Cell className={per90Cell}>-</Cell>
                {statNames.map((n) => {
                  const elementStat = getElementStat(n);
                  return (
                    <Cell className={per90Cell} key={n}>
                      {elementStat?.per90Stat &&
                      elementStat.per90Stat !== "starts_per_90"
                        ? element[elementStat.per90Stat]
                        : "-"}
                    </Cell>
                  );
                })}
                <Cell className={per90Cell}>-</Cell>
                <Cell className={per90Cell}>-</Cell>
                <Cell className={per90Cell}>-</Cell>
              </Row>
            </TableBody>
          </Table>
        </div>
      ) : (
        <SurfaceContainer>
          <p>
            {t(
              "eventHistoryTable.noSeasonData",
              "Data will appear here once the season is underway.",
            )}
          </p>
        </SurfaceContainer>
      )}
    </div>
  );
};

export default EventHistoryTable;
